import z from "zod";
import {
	BetPickSchema,
	BetStatusSchema,
	EnhancingTagSchema,
	EventStatusSchema,
	PrizepicksEnhancingTagSchema,
	SettledStatValueSchema,
	SportKeySchema,
  WinningOutcomeIdSchema,
} from "./plain-schemas";
import {
	ExactNumberOddsSnapshotSchema,
	MoneylineOddsSnapshotSchema,
	RangeBucketOddsSnapshotSchema,
	SpreadOddsSnapshotSchema,
	StatTypeSchema,
	TotalOddsSnapshotSchema,
	YesNoOddsSnapshotSchema,
} from "./odds-plain-schemas";

export const UserDbSchema = z.object({
	id: z.string(),
	embedded_wallet_address: z.string(),
	privy_wallet_id: z.string().optional(),
	privy_user_id: z.string().optional(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
	linked_wallet_address: z.string().optional(),
	avatar_filepath: z.string().optional(),
	avatar_url: z.string().optional(),
	metadata: z.object({
		is_onboarding_done: z.boolean().optional(),
	}).default({}),
});

const BaseDbSchema = z.object({
	id: z.string(),
	updated_at: z.coerce.date(),
	created_at: z.coerce.date(),
});

export const FtpStreakCheckinDbSchema = BaseDbSchema.merge(z.object({
	user_id: z.string(),
	check_in_day_key: z.string(),
	check_in_date: z.coerce.date(),
	metadata: z.object({
		last_claimed_milestone_day: z.number().optional(),
	}).default({}),
}));

const BaseOddsSnapshotDbSchema = BaseDbSchema.merge(
	z.object({
		optic_odds_market_id: z.string(),
		metadata: z.object({
			is_replicated: z.boolean().optional(),
			is_player_market: z.boolean().optional(),
			allow_total_one_outcome: z.boolean().optional(),
			prizepicks_enhancing_tag: PrizepicksEnhancingTagSchema.optional(),
			enhancing_tag: EnhancingTagSchema.optional(),
		}).default({}),
	})
);
export const OddsSnapshotDbSchema = z.discriminatedUnion("market_kind", [
	MoneylineOddsSnapshotSchema.merge(BaseOddsSnapshotDbSchema),
	TotalOddsSnapshotSchema.merge(BaseOddsSnapshotDbSchema),
	SpreadOddsSnapshotSchema.merge(BaseOddsSnapshotDbSchema),
	YesNoOddsSnapshotSchema.merge(BaseOddsSnapshotDbSchema),
	ExactNumberOddsSnapshotSchema.merge(BaseOddsSnapshotDbSchema),
	RangeBucketOddsSnapshotSchema.merge(BaseOddsSnapshotDbSchema),
]);

export const SportDbSchema = z.object({
	id: z.string(),
	optic_odds_id: z.string().optional(),
	key: SportKeySchema,
	odds_api_id: z.string().optional(),
	title: z.string(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
	metadata: z.object({

	}).default({})
});

export const LeagueDbSchema = z.object({
	id: z.string(),
	optic_odds_id: z.string().optional(),
	odds_api_id: z.string().optional(),
	the_odds_api_id: z.string().optional(),
	title: z.string(),
	sport_id: z.string(),
	image_url: z.string().optional(),
	image_filepath: z.string().optional(),
	active_event_markets_count: z.number().default(0),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
	metadata: z.object({
		gender: z.enum(["male", "female", "mixed"]).optional(),
		region_code: z.string().optional(),
	}).default({}),
});

export const BaseParticipantDbSchema = z.object({
	id: z.string(),
	optic_odds_id: z.string().optional(),
	base_id: z.string().nullable(),
	sport_id: z.string(),
	league_id: z.string(),
	title: z.string(),
	image_url: z.string().nullable(),
	image_filepath: z.string().optional(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
	revalidated_at: z.coerce.date(),
	metadata: z.object({
		is_optic_odds_team: z.boolean().optional(),
		last_x_synced_till: z.coerce.date().optional(),
		needs_manual_review: z.boolean().optional(),
		is_image_processed: z.boolean().optional(),
		orig_oo_image_url: z.string().optional(),
	}).default({}),
});

export const PlayerStatsHistoricalRecordDbSchema = z.object({
	id: z.string(),
	sport_id: z.string(),
	league_id: z.string(),

	participant_id: z.string(),
	participant_base_id: z.string(),

	optic_odds_fixture_id: z.string(),
	event_id: z.string(),
	// we can cache it here as we use ONLY past events in this doc
	event_commence_date: z.coerce.date(),

	raw_mstats: z.record(z.string(), z.union([ z.number(), z.null() ])).nullable(),
	raw_stats: z.record(z.string(), z.union([ z.number(), z.null() ])).nullable(),
	stats_settled_values: z.record(StatTypeSchema, z.union([ SettledStatValueSchema, z.null() ])).nullable(),

	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),

	metadata: z.object({

	}).default({}),
});

export const TeamParticipantDbSchema = z.object({
	type: z.literal("team"),
	abbreviation: z.string().nullable(),
	city: z.string().nullable(),
}).merge(BaseParticipantDbSchema);

export const PlayerParticipantDbSchema = z.object({
	type: z.literal("player"),
	team_id: z.string().nullable(),
	age: z.number().nullable(),
	height: z.coerce.number().nullable(),
	weight: z.coerce.number().nullable(),
	position: z.string().nullable(),
	number: z.coerce.number().nullable(),
	first_name: z.string(),
	last_name: z.string(),
}).merge(BaseParticipantDbSchema);

export const ParticipantDbSchema = z.discriminatedUnion("type", [
	TeamParticipantDbSchema,
	PlayerParticipantDbSchema,
]);

type ParticipantSchemaMap = {
	team: typeof TeamParticipantDbSchema;
	player: typeof PlayerParticipantDbSchema;
};
export type ParticipantAs<T extends keyof ParticipantSchemaMap> = z.infer<ParticipantSchemaMap[T]>;

export const EventDbSchema = z.object({
	id: z.string(),
	sport_id: z.string(),
	league_id: z.string(),
	optic_odds_id: z.string().optional(),
	home_id: z.string().nullable(),
	away_id: z.string().nullable(),
	title: z.string(),
	status: EventStatusSchema,
	markets_count: z.number().default(0),
	major_reschedules_count: z.number().default(0),
	reschedules_history: z.array(z.object({
		old_commence_date: z.coerce.date(),
		new_commence_date: z.coerce.date(),
		is_major: z.boolean(),
		rescheduled_at: z.coerce.date(),
	})).default([]),
	status_changes_history: z.array(z.object({
		from_status: EventStatusSchema,
		to_status: EventStatusSchema,
		changed_at: z.coerce.date(),
	})).default([]),

	commence_date: z.coerce.date(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

const exclude_from_odds_snapshot = {
	bookmaker_key: true,
	bookmaker_title: true,
	market_id: true,
	market_onchain_id: true,
	market_title: true,
	market_base_id: true,
} as const;
const BaseEventMarketDbSchema = z.object({
	base_id: z.string(),
	onchain_id: z.string(),
	title: z.string(),
	event_title: z.string().default("---"),
	used_bookmaker_key: z.string(),
	commence_date: z.coerce.date(),
	event_status: EventStatusSchema,
	event_major_reschedule_index: z.number().default(0),
	settled_stat_value: SettledStatValueSchema.optional(),
	winning_outcome_id: WinningOutcomeIdSchema.optional(),
	optic_odds_market_id: z.string(),
	onchain_status: z.enum(["pending", "settled", "voided"]).default("pending"),
	onchain_min_settlement_date: z.coerce.date().optional(),
	is_created_onchain: z.boolean().default(false),
	is_inactive: z.boolean().default(false),
	metadata: z.object({
		is_player_market: z.boolean().optional(),
		onchain_settler_repeat_after: z.coerce.date().optional(),
		onchain_settler_postpones_count: z.number().optional(),
		onchain_settler_last_iterations_chunk_id: z.string().optional(),
		is_custom_fv_determiner_used: z.boolean().optional(),
		enhancing_tag: EnhancingTagSchema.optional(),
		allow_total_one_outcome: z.boolean().optional(),
		total_wagered_usd: z.number().optional(),
		total_bets_count: z.number().optional(),
		outcome_bets_count: z.record(z.string(), z.number()).optional(),
		last_counted_bet_at: z.coerce.date().optional(),
	}).default({}),
});
export const EventMarketDbSchema = z.discriminatedUnion("market_kind", [
	MoneylineOddsSnapshotSchema.omit(exclude_from_odds_snapshot)
		.merge(BaseDbSchema)
		.merge(BaseEventMarketDbSchema),
	TotalOddsSnapshotSchema.omit(exclude_from_odds_snapshot)
		.merge(BaseDbSchema)
		.merge(BaseEventMarketDbSchema),
	SpreadOddsSnapshotSchema.omit(exclude_from_odds_snapshot)
		.merge(BaseDbSchema)
		.merge(BaseEventMarketDbSchema),
	YesNoOddsSnapshotSchema.omit(exclude_from_odds_snapshot)
		.merge(BaseDbSchema)
		.merge(BaseEventMarketDbSchema),
	ExactNumberOddsSnapshotSchema.omit(exclude_from_odds_snapshot)
		.merge(BaseDbSchema)
		.merge(BaseEventMarketDbSchema),
	RangeBucketOddsSnapshotSchema.omit(exclude_from_odds_snapshot)
		.merge(BaseDbSchema)
		.merge(BaseEventMarketDbSchema),
]);

export const BetPickDbSchema = z.object({
	event_market_id: z.string(),
	outcome_id: z.string(),
});

const BetDbBaseSchema = z.object({
		onchain_id: z.string(),
		owner_wallet_address: z.string(),
		contract_address: z.string(),
		chain_id: z.number(),
		size_raw: z.coerce.bigint(),
		size: z.number(),
		settlement_info: z.object({
			payout_amount_raw: z.coerce.bigint(),
			payout_amount: z.number(),
			is_winner: z.boolean(),
		}).optional().nullable(),
		placed_at: z.coerce.date(),
		status: BetStatusSchema,
		wallet_nonce: z.number(),
		markets_grouping_key: z.string(),
		metadata: z.object({
			onchain_settler_repeat_after: z.coerce.date().optional(),
			onchain_settler_anchor_em_id: z.string().optional(),
			onchain_settler_postpones_count: z.number().optional(),
			onchain_settler_last_iterations_chunk_id: z.string().optional(),
			cancel_requested_at: z.coerce.date().optional(),
			cancel_deadline_ts: z.number().optional(),
			cancel_wallet_nonce: z.number().optional(),
			cancel_owner_signature: z.string().optional(),
			cancel_automated_authority_signature: z.string().optional(),
			last_cancel_fail_reason: z.string().optional(),
		}).default({}),
	});

const BetDbPowerplaySchema = BetDbBaseSchema.merge(z.object({
	type: z.literal("powerplay"),
	multiplier: z.number(),
	multiplier_raw: z.coerce.bigint(),
	picks: z.array(BetPickDbSchema.merge(z.object({
		event_market_onchain_id: z.string(),
		outcome_onchain_id: z.string(),
	}))),
	combo_multipliers_config_onchain_id: z.string(),
})).merge(BaseDbSchema);

const BetDbFlexplaySchema = BetDbBaseSchema.merge(z.object({
	type: z.literal("flexplay"),
	max_multiplier: z.number(),
	max_multiplier_raw: z.coerce.bigint(),
	picks: z.array(BetPickDbSchema.merge(z.object({
		event_market_onchain_id: z.string(),
		outcome_onchain_id: z.string(),
	}))),
	flex_combo_multipliers_config_onchain_id: z.string(),
})).merge(BaseDbSchema);

export const BetDbSchema = z.discriminatedUnion("type", [
	BetDbPowerplaySchema,
	BetDbFlexplaySchema,
]);

export const BetShareLinkDbSchema = z.object({
	id: z.string(),
	share_code: z.string().min(1).max(5),
	bet_id: z.string(),
	owner_wallet_address: z.string().optional(),
	expires_at: z.coerce.date(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
	metadata: z.object({
		clicks_count: z.number().int().nonnegative().optional(),
		last_clicked_at: z.coerce.date().optional(),
	}).default({}),
});

export const CorrelatedStatsRestrictionDbSchema = z.object({
	id: z.string(),
	left_stat_type: StatTypeSchema,
	right_stat_type: StatTypeSchema,
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

export const BetEventDbSchema = z.object({
	id: z.string(),
	bet_onchain_id: z.string(),

	body: z.discriminatedUnion("type", [
		z.object({
			type: z.literal("created"),
			owner_wallet_address: z.string(),
			size_raw: z.coerce.bigint(),
			size: z.number(),
			wallet_nonce: z.number(),
			variant: z.discriminatedUnion("bet_type", [
				z.object({
					bet_type: z.literal("powerplay"),
					combo_multipliers_config_onchain_id: z.string(),
					picks: z.array(
						z.object({
							event_market_id: z.string(),
							event_market_onchain_id: z.string(),
							outcome_id: z.string(),
							outcome_onchain_id: z.string(),
						})
					),
				}),
				z.object({
					bet_type: z.literal("flexplay"),
					flex_combo_multipliers_config_onchain_id: z.string(),
					picks: z.array(
						z.object({
							event_market_id: z.string(),
							event_market_onchain_id: z.string(),
							outcome_id: z.string(),
							outcome_onchain_id: z.string(),
						})
					),
				}),
			]),
		}),
		z.object({
			type: z.literal("status-changed"),
			status: z.enum(["frozen", "refunded", "seized", "active"]),
		}),
		z.object({
			type: z.literal("user-canceled"),
			owner_wallet_address: z.string(),
			refund_amount_raw: z.coerce.bigint(),
			refund_amount: z.number(),
			fee_amount_raw: z.coerce.bigint(),
			fee_amount: z.number(),
			fee_bps: z.number(),
		}),
		z.object({
			type: z.literal("settled"),
			payout_amount_raw: z.coerce.bigint(),
			payout_amount: z.number(),
			is_winner: z.boolean(),
			variant: z.discriminatedUnion("bet_type", [
				z.object({
					bet_type: z.literal("powerplay"),
				}),
				z.object({
					bet_type: z.literal("flexplay"),
				}),
			]),
		}),
	]),

	contract_address: z.string(),
	tx_hash: z.string(),
	chain_id: z.number(),
	block_number: z.number(),
	block_hash: z.string(),
	lamport_timestamp: z.coerce.bigint(),
	is_orphaned: z.boolean().default(false),
	block_date: z.coerce.date(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

export const ComboMultipliersConfigEventDbSchema = z.object({
	id: z.string(),
	multipliers_config_onchain_id: z.string(),

	body: z.discriminatedUnion("type", [
		z.object({
			type: z.literal("created"),
			multipliers_raw: z.array(z.coerce.bigint()),
			multipliers: z.array(z.number()),
			offset: z.number().min(0),
		}),
		z.object({
			type: z.literal("updated"),
			is_active: z.boolean(),
			multipliers_raw: z.array(z.coerce.bigint()),
			multipliers: z.array(z.number()),
		}),
	]),

	contract_address: z.string(),
	tx_hash: z.string(),
	chain_id: z.number(),
	block_number: z.number(),
	block_hash: z.string(),
	lamport_timestamp: z.coerce.bigint(),
	is_orphaned: z.boolean().default(false),
	block_date: z.coerce.date(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

export const FlexComboMultipliersConfigEventDbSchema = z.object({
	id: z.string(),
	multipliers_config_onchain_id: z.string(),

	body: z.discriminatedUnion("type", [
		z.object({
			type: z.literal("created"),
			multipliers_by_lost_picks_raw: z.array(z.array(z.coerce.bigint())),
			multipliers_by_lost_picks: z.array(z.array(z.number())),
			offset: z.number().min(0),
		}),
		z.object({
			type: z.literal("updated"),
			is_active: z.boolean(),
			multipliers_by_lost_picks_raw: z.array(z.array(z.coerce.bigint())),
			multipliers_by_lost_picks: z.array(z.array(z.number())),
		}),
	]),

	contract_address: z.string(),
	tx_hash: z.string(),
	chain_id: z.number(),
	block_number: z.number(),
	block_hash: z.string(),
	lamport_timestamp: z.coerce.bigint(),
	is_orphaned: z.boolean().default(false),
	block_date: z.coerce.date(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

export const EventMarketEventDbSchema = z.object({
	id: z.string(),
	event_market_onchain_id: z.string(),

	body: z.discriminatedUnion("type", [
		z.object({
			type: z.literal("created"),
			min_settlement_date: z.coerce.date(),
		}),
		z.object({
			type: z.literal("settled"),
			winning_outcome_onchain_id: z.string(),
		}),
		z.object({
			type: z.literal("voided"),
		}),
		z.object({
			type: z.literal("updated"),
			min_settlement_date: z.coerce.date(),
		}),
	]),

	contract_address: z.string(),
	tx_hash: z.string(),
	chain_id: z.number(),
	block_number: z.number(),
	block_hash: z.string(),
	lamport_timestamp: z.coerce.bigint(),
	is_orphaned: z.boolean().default(false),
	block_date: z.coerce.date(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

export const ComboMultipliersConfigDbSchema = z.object({
	id: z.string(),
	onchain_id: z.string(),
	multipliers_raw: z.array(z.coerce.bigint()),
	multipliers: z.array(z.number()),
	offset: z.number().min(0),
	is_active: z.boolean(),
	chain_id: z.number(),
	contract_address: z.string(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

export const FlexComboMultipliersConfigDbSchema = z.object({
	id: z.string(),
	onchain_id: z.string(),
	multipliers_by_lost_picks_raw: z.array(z.array(z.coerce.bigint())),
	multipliers_by_lost_picks: z.array(z.array(z.number())),
	offset: z.number().min(0),
	is_active: z.boolean(),
	chain_id: z.number(),
	contract_address: z.string(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

const QueuedBetDbBaseSchema = BaseDbSchema.merge(z.object({
	picks: z.array(BetPickSchema),
	size_raw: z.string(),
	wallet_nonce: z.number().int().nonnegative(),
	initiator_signature: z.string(),
	owner_wallet_address: z.string(),
	contract_address: z.string(),
	chain_id: z.number(),
	deadline_ts: z.number().int().nonnegative(),
	token_type: z.number().int().nonnegative().max(1),

	status: z.enum(["queued", "failed", "finalized"]),
	deterministic_id: z.string(),
	tx_hash: z.string().optional(),
	automated_authority_signature: z.string().optional(),
	size: z.number(),
	metadata: z.object({

	}).default({}),
}));

const QueuedPowerplayBetDbSchema = QueuedBetDbBaseSchema.merge(z.object({
	type: z.literal("powerplay"),
	combo_multipliers_config_onchain_id: z.string(),
}));
const QueuedFlexplayBetDbSchema = QueuedBetDbBaseSchema.merge(z.object({
	type: z.literal("flexplay"),
	flex_combo_multipliers_config_onchain_id: z.string(),
}));

export const QueuedBetDbSchema = z.discriminatedUnion("type", [
	QueuedPowerplayBetDbSchema,
	QueuedFlexplayBetDbSchema,
]);
